import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userRole: '',
    name: '',
    email: '',
    password: '',
    organization: '',
    logo: null as File | null,
    agreeToTerms: false,
    aircraftOption: 'with-sensor',
    aircraftType: '',
    aircraftIdentifier: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [aircraftList, setAircraftList] = useState<Array<{
    type: string;
    identifier: string;
    withSensor: boolean;
  }>>([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: target.checked,
      });
    } else if (type === 'file') {
      const target = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: target.files ? target.files[0] : null,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });

      // Reset aircraft list when changing aircraft option
      if (name === 'aircraftOption') {
        setAircraftList([]);
        setFormData({
          ...formData,
          [name]: value,
          aircraftType: '',
          aircraftIdentifier: '',
        });
        return;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('userRole', formData.userRole);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('organization', formData.organization);

      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }

      // Add aircraft list as JSON string
      if (aircraftList.length > 0) {
        formDataToSend.append('aircraft', JSON.stringify(aircraftList));
      }

      // Send registration request to backend
      const response = await apiRequest('/register', {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Registration successful - redirect to login
      alert('Registrierung erfolgreich! Sie können sich jetzt anmelden.');
      navigate('/login');

    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  const isAircraftDetailsEnabled = formData.aircraftOption === 'with-sensor' || formData.aircraftOption === 'without-sensor';

  // Check if aircraft form is valid for saving
  const isAircraftFormValid =
    formData.aircraftType !== '' &&
    formData.aircraftIdentifier.trim() !== '';

  // Check if aircraft requirements are met for registration
  const isAircraftRequirementMet =
    formData.aircraftOption === 'none' || // No aircraft required
    (isAircraftDetailsEnabled && aircraftList.length > 0); // Aircraft required and at least one added

  // Check if all required fields are filled
  const isFormValid =
    formData.userRole !== '' &&
    formData.name.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.password.length >= 8 &&
    formData.agreeToTerms &&
    isAircraftRequirementMet;

  const handleSaveAircraft = () => {
    // Add aircraft to list
    const newAircraft = {
      type: formData.aircraftType,
      identifier: formData.aircraftIdentifier,
      withSensor: formData.aircraftOption === 'with-sensor',
    };

    setAircraftList([...aircraftList, newAircraft]);

    // Reset form fields for next aircraft
    setFormData({
      ...formData,
      aircraftType: '',
      aircraftIdentifier: '',
    });
  };

  const handleRemoveAircraft = (index: number) => {
    setAircraftList(aircraftList.filter((_, i) => i !== index));
  };

  return (
    <div className="container">
      <h1 className="text-center my-4">Registrierung</h1>
      <div className="row">
        <div className="col-12 col-lg-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-body p-4">
              <h2 className="card-title mb-4">Konto</h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="userRole" className="form-label">
                    Nutzerrolle <span className="text-danger">*</span>
                  </label>
                  <select
                    id="userRole"
                    name="userRole"
                    className="form-select"
                    value={formData.userRole}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Bitte wählen</option>
                    <option value="flugschule">Flugschule</option>
                    <option value="pilot">Pilot</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    Passwort <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="form-control"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                  />
                  <div className="form-text">Mindestens 8 Zeichen</div>
                </div>

                <div className="mb-3">
                  <label htmlFor="organization" className="form-label">
                    Organisation
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    className="form-control"
                    value={formData.organization}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="logo" className="form-label">
                    Logo
                  </label>
                  <input
                    type="file"
                    id="logo"
                    name="logo"
                    className="form-control"
                    onChange={handleChange}
                    accept="image/*"
                  />
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    className="form-check-input"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="agreeToTerms" className="form-check-label">
                    Ich stimme den Datenschutz- und AGB-Bestimmungen zu <span className="text-danger">*</span>
                  </label>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={!isFormValid || isLoading}
                >
                  {isLoading ? 'Wird registriert...' : 'Konto erstellen'}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-body p-4">
              <h2 className="card-title mb-4">Flugobjekte</h2>

              <div className="mb-3">
                <div className="form-check mb-2">
                  <input
                    type="radio"
                    id="noAircraft"
                    name="aircraftOption"
                    value="none"
                    className="form-check-input"
                    checked={formData.aircraftOption === 'none'}
                    onChange={handleChange}
                  />
                  <label htmlFor="noAircraft" className="form-check-label">
                    Ich registriere mich zunächst ohne Flugobjekte.
                  </label>
                </div>

                <div className="form-check mb-2">
                  <input
                    type="radio"
                    id="withSensor"
                    name="aircraftOption"
                    value="with-sensor"
                    className="form-check-input"
                    checked={formData.aircraftOption === 'with-sensor'}
                    onChange={handleChange}
                  />
                  <label htmlFor="withSensor" className="form-check-label">
                    Ich möchte Flugobjekte anlegen und Sensoren bestellen.
                  </label>
                </div>

                <div className="form-check mb-3">
                  <input
                    type="radio"
                    id="withoutSensor"
                    name="aircraftOption"
                    value="without-sensor"
                    className="form-check-input"
                    checked={formData.aircraftOption === 'without-sensor'}
                    onChange={handleChange}
                  />
                  <label htmlFor="withoutSensor" className="form-check-label">
                    Ich möchte Flugobjekte anlegen, aber vorerst keine Sensoren bestellen.
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="aircraftType" className="form-label">
                  Typ {isAircraftDetailsEnabled && <span className="text-danger">*</span>}
                </label>
                <select
                  id="aircraftType"
                  name="aircraftType"
                  className="form-select"
                  value={formData.aircraftType}
                  onChange={handleChange}
                  disabled={!isAircraftDetailsEnabled}
                >
                  <option value="">Bitte wählen</option>
                  <option value="balloon">Ballon</option>
                  <option value="glider">Segelflugzeug</option>
                  <option value="plane">Motorflugzeug</option>
                  <option value="helicopter">Hubschrauber</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="aircraftIdentifier" className="form-label">
                  Kennung {isAircraftDetailsEnabled && <span className="text-danger">*</span>}
                </label>
                <input
                  type="text"
                  id="aircraftIdentifier"
                  name="aircraftIdentifier"
                  className="form-control"
                  value={formData.aircraftIdentifier}
                  onChange={handleChange}
                  disabled={!isAircraftDetailsEnabled}
                />
              </div>

              <div className={`mb-4 ${!isAircraftDetailsEnabled ? 'opacity-50' : ''}`}>
                <div className="row">
                  <div className="col-md-4 mb-3 mb-md-0">
                    <div className="bg-light border rounded d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
                      <span className="text-muted">Sensorkit</span>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <p className="mb-2">
                      Das Sensorkit enthält eine gedruckte Montageanleitung, die in Ihr Flughandbuch aufgenommen werden muss.
                      Für eine frühzeitige Vorbereitung steht Ihnen die Anleitung auch vorab digital zur Verfügung.
                    </p>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={!isAircraftDetailsEnabled}
                    >
                      Montageanleitung
                    </button>
                  </div>
                </div>
              </div>

              <div className="d-grid gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveAircraft}
                  disabled={!isAircraftDetailsEnabled || !isAircraftFormValid}
                >
                  Hinzufügen
                </button>
              </div>

              {/* List of added aircraft */}
              {aircraftList.length > 0 && (
                <div className="mt-4">
                  <h5 className="mb-3">Hinzugefügte Flugobjekte</h5>
                  <div className="d-flex flex-column gap-2">
                    {aircraftList.map((aircraft, index) => (
                      <div key={index} className="card">
                        <div className="card-body p-3 d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{aircraft.type === 'balloon' ? 'Ballon' : aircraft.type === 'glider' ? 'Segelflugzeug' : aircraft.type === 'plane' ? 'Motorflugzeug' : 'Hubschrauber'}</strong>
                            {' - '}
                            <span>{aircraft.identifier}</span>
                            <br />
                            <small className="text-muted">
                              {aircraft.withSensor ? 'Mit Sensor' : 'Ohne Sensor'}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveAircraft(index)}
                            aria-label="Flugobjekt entfernen"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
